/**
 * FP&A DRE module contracts (ALC-1903 / epic ALC-1902).
 *
 * Frozen in slice S0 so slices A–G can compile against them in parallel.
 * Design of record: docs/adr/0006..0009-fpa-*.md and
 * packages/services/src/fpa/CONTEXT.md.
 */
import type {
  AccountType,
  FilterLogic,
  FilterOperator,
  FpaDateBasis,
  FpaDisplayFormat,
  FpaFilterDimension,
  FpaFonte,
  FpaForecastRuleSetStatus,
  FpaForecastRuleStatus,
  FpaForecastRuleVariant,
  FpaHeadcountHiringType,
  FpaHeadcountPositionStatus,
  FpaMetricProducer,
  FpaRegime,
  FpaRollupKind,
  FpaSignConvention,
  FpaTemplateRowType,
} from './generated/enums.js';

// ── Periods ──────────────────────────────────────────────────────────────────

/** Calendar month key, `YYYY-MM`. The FP&A period grain for evaluation and metrics. */
export type FpaPeriodKey = string;

// ── Formula AST ──────────────────────────────────────────────────────────────
// Same shape as packages/types/src/ap/rollups.ts FPARollupFormulaNode, extended
// with metric_ref and prior_period (epic decision). No cross-template refs.

export type FpaFormulaNode =
  | { type: 'rollup_ref'; rollupId: string }
  | { type: 'metric_ref'; metricKey: string }
  | { type: 'assumption_ref'; assumptionKey: string }
  | { type: 'prior_period'; operand: FpaFormulaNode }
  | { type: 'constant'; value: number }
  | {
      type: 'binary_op';
      operator: '+' | '-' | '*' | '/';
      left: FpaFormulaNode;
      right: FpaFormulaNode;
    }
  | { type: 'unary_op'; operator: '-'; operand: FpaFormulaNode }
  | { type: 'paren'; expression: FpaFormulaNode };

/**
 * Well-known Premissa key for the global salary-raise knob (ALC-2118).
 *
 * Values are FRACTIONS (`0.05` = +5%), never percent points (`5` is rejected).
 * A rate entered at month M applies from M onward; multiple raise months
 * compound multiplicatively. Applied to forward Plano de Contratações months
 * after real-salary supersession, never to realized payroll months.
 * Persist and the engine both refuse rate ≤ −1 or rate > 10
 * (`FPA_HEADCOUNT_SALARY_RAISE_INVALID`).
 */
export const FPA_HEADCOUNT_SALARY_RAISE_ASSUMPTION_KEY = 'headcount.salary_raise';

// ── Rollups ──────────────────────────────────────────────────────────────────

export type FpaRollupFilterValue =
  | { value: string | number }
  | { values: (string | number)[] }
  | { pattern: string }
  | { from: number; to: number }
  | { segmentId: string; values: string[] };

export interface FpaRollupFilterInput {
  sortOrder?: number;
  logic?: FilterLogic;
  dimension: FpaFilterDimension;
  operator: FilterOperator;
  value: FpaRollupFilterValue;
}

export interface FpaRollupFilterResponse extends FpaRollupFilterInput {
  filterId: string;
  sortOrder: number;
  logic: FilterLogic;
}

/** Explicit account selection; exactly one id is set, matching the rollup's fonte. */
export interface FpaRollupAccountRef {
  glAccountId?: string | null;
  managerialAccountId?: string | null;
}

/**
 * How a tax-slot clause combines with the rest of an AGGREGATION.
 * INCLUDE is its own population, unioned with account types and explicit accounts.
 * EXCLUDE removes matches. Neither clause widens a type rule by itself.
 */
export const FpaTaxSlotClauseMode = {
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;
export type FpaTaxSlotClauseMode = (typeof FpaTaxSlotClauseMode)[keyof typeof FpaTaxSlotClauseMode];

/**
 * Dynamic tax-slot selection. The clause stores slot codes, never GL account ids.
 * Evaluation resolves the active mapping for the row's entity and that entity's
 * current CoA version, then intersects with `accountTypes` when that list is set.
 * An empty `accountTypes` list means any type of the mapped account.
 * Contábil only — Gerencial rollups reject these clauses.
 */
export interface FpaRollupTaxSlotClause {
  mode: FpaTaxSlotClauseMode;
  slots: string[];
  accountTypes: AccountType[];
}

export interface FpaRollupResponse {
  rollupId: string;
  name: string;
  description?: string | null;
  parentRollupId?: string | null;
  source: FpaFonte;
  kind: FpaRollupKind;
  accountTypes: AccountType[];
  formula?: FpaFormulaNode | null;
  format: FpaDisplayFormat;
  isStandard: boolean;
  standardKey?: string | null;
  accounts: FpaRollupAccountRef[];
  filters: FpaRollupFilterResponse[];
  /** Empty when the rollup does not select by tax slot. */
  taxSlotClauses?: FpaRollupTaxSlotClause[];
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export interface FpaCreateRollupRequest {
  name: string;
  description?: string | null;
  parentRollupId?: string | null;
  source: FpaFonte;
  kind: FpaRollupKind;
  accountTypes?: AccountType[];
  formula?: FpaFormulaNode | null;
  format?: FpaDisplayFormat;
  accounts?: FpaRollupAccountRef[];
  filters?: FpaRollupFilterInput[];
  taxSlotClauses?: FpaRollupTaxSlotClause[];
}

export interface FpaUpdateRollupRequest {
  name?: string;
  description?: string | null;
  parentRollupId?: string | null;
  accountTypes?: AccountType[];
  formula?: FpaFormulaNode | null;
  format?: FpaDisplayFormat;
  accounts?: FpaRollupAccountRef[];
  filters?: FpaRollupFilterInput[];
  /** Full replacement when provided. Omitted leaves the stored clauses unchanged. */
  taxSlotClauses?: FpaRollupTaxSlotClause[];
  /**
   * Explicit restatement consent (ALC-1910, CFO-01): required together with
   * `changeReason` when the rollup feeds a template that already has frozen
   * snapshots, because live evaluation would restate a reported report.
   */
  allowReportedPeriodChange?: boolean;
  /** Journaled onto the definition revision. */
  changeReason?: string | null;
}

export interface FpaListRollupsQuery {
  source?: FpaFonte;
  kind?: FpaRollupKind;
  includeDeleted?: boolean;
}

// ── Templates ────────────────────────────────────────────────────────────────

export interface FpaTemplateRowInput {
  rowType: FpaTemplateRowType;
  rollupId?: string | null;
  label?: string | null;
  sortOrder?: number;
  bold?: boolean;
  indentLevel?: number;
  signConvention?: FpaSignConvention;
}

export interface FpaTemplateRowResponse extends FpaTemplateRowInput {
  rowId: string;
  sortOrder: number;
  bold: boolean;
  indentLevel: number;
  signConvention: FpaSignConvention;
}

/** Currency-cell scaling for the DRE grid. */
export type FpaAmountScale = 'FULL' | 'THOUSANDS' | 'MILLIONS';

/**
 * Report display options persisted on the template ("Opções de visualização").
 * Presentation-only — they scale/format rendering and never affect evaluated
 * values, so changing them does not demand restatement consent. Null (or an
 * omitted key) means the default: full numbers, standard % precision, no sign
 * colors, currency symbol shown.
 */
export interface FpaTemplateDisplaySettings {
  amountScale?: FpaAmountScale;
  /** Decimal places on percentage cells (0–4). */
  percentDecimals?: number;
  /** Color positive/negative values (font color only). */
  signColors?: boolean;
  /** Show the currency symbol on amounts. */
  showCurrency?: boolean;
  /** Show cents for full currency amounts. */
  showCents?: boolean;
  /**
   * Show the Não Classificado bucket row (ALC-2116, CTL-21: "hide the row when
   * presenting"). Absent or `true` means shown, so every template written before
   * this key existed keeps today's behaviour — only an explicit `false` hides it.
   *
   * Presentation-only like every other key here: NEITHER evaluator reads it — both
   * always emit the bucket — and the client filters the row at render time
   * (`visibleFpaBuckets`). That keeps the evaluated payload reconciling to the full
   * P&L and row-for-row comparable to a frozen snapshot, which is why persisting
   * this key costs no restatement consent (`template.service.ts`).
   *
   * Hiding is all-or-nothing across every column, and the bucket is a reconciliation
   * row that is never summed into a rollup or CALCULATED total, so suppressing it
   * cannot change another cell's value.
   */
  showNaoClassificado?: boolean;
}

export interface FpaTemplateResponse {
  templateId: string;
  name: string;
  description?: string | null;
  regime: FpaRegime;
  fonte: FpaFonte;
  dateBasis?: FpaDateBasis | null;
  /** Persisted scenario-axis defaults; request-time columns override these. */
  defaultColumns?: FpaColumnSet[] | null;
  /** Persisted DRE display options; null = defaults. */
  displaySettings?: FpaTemplateDisplaySettings | null;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface FpaTemplateDetailResponse extends FpaTemplateResponse {
  rows: FpaTemplateRowResponse[];
}

export interface FpaCreateTemplateRequest {
  name: string;
  description?: string | null;
  regime: FpaRegime;
  fonte: FpaFonte;
  /** Competência templates only; rejected otherwise. */
  dateBasis?: FpaDateBasis | null;
  defaultColumns?: FpaDefaultColumnInput[] | null;
  displaySettings?: FpaTemplateDisplaySettings | null;
  rows?: FpaTemplateRowInput[];
}

/**
 * Post-creation template updates: rows, name, and description only.
 * Regime, fonte, and dateBasis are immutable (ADR 0007) — the API rejects them.
 */
export interface FpaUpdateTemplateRequest {
  name?: string;
  description?: string | null;
  /** Full replacement of the persisted scenario-axis defaults. */
  defaultColumns?: FpaDefaultColumnInput[] | null;
  /** Full replacement of the display options; omitted-means-unchanged, null resets to defaults. */
  displaySettings?: FpaTemplateDisplaySettings | null;
  /** Full replacement of the ordered row list. */
  rows?: FpaTemplateRowInput[];
  /**
   * Explicit restatement consent (ALC-1910, CFO-01): required together with
   * `changeReason` when the template already has frozen snapshots, because
   * live evaluation would restate a reported report.
   */
  allowReportedPeriodChange?: boolean;
  /** Journaled onto the definition revision. */
  changeReason?: string | null;
}

export interface FpaListTemplatesQuery {
  regime?: FpaRegime;
  fonte?: FpaFonte;
  dateBasis?: FpaDateBasis;
  includeArchived?: boolean;
}

// ── Scenarios (ADR 0008) ─────────────────────────────────────────────────────

/**
 * Built-in scenario-provider keys. The axis is open (ADR 0008): the string
 * `kind` on {@link FpaScenarioRef} is a provider key resolved through the
 * scenario provider registry, so future forecast engines register a new key
 * without touching this module or the schema.
 */
export const FpaScenarioKind = {
  REALIZADO: 'REALIZADO',
  ORCADO: 'ORCADO',
  REFORECAST: 'REFORECAST',
  /**
   * Forward revenue curve by competência (ALC-1913): accrual revenue projected
   * from ACTIVE billing contracts through the recognition-aligned engine in
   * `packages/services/src/fpa/revenue-projection/`. Revenue-only by design —
   * expense rollups receive no cells from this provider. Accepts
   * `params.assumedRenewalTerms` (default 0) and `params.dynamicPtax` (ALC-2625,
   * default `false`, boolean): OFF keeps every RECOGNIZED row at its booked
   * value; ON additionally remeasures RECOGNIZED rows on a `USAGE_ASSET`
   * schedule (an unsettled Contract Asset) at today's PTAX, surfacing the delta
   * as its own `projected_revenue_fx_remeasurement` drill-down row. Settled
   * (`ADVANCE_LIABILITY`) rows are never retranslated either way; future/
   * unrecognized months always use today's PTAX regardless of this option.
   */
  RECEITA_PROJETADA: 'RECEITA_PROJETADA',
  /**
   * Forward expense curve by competência (ALC-2116): accrual expenses projected
   * from a user-authored {@link FpaForecastRuleSetResponse} — recurring vendor bill
   * templates, existing-but-unposted documents and open purchase orders, plus the
   * ad-hoc historical residual. Expense-only by design. Carries
   * `forecastRuleSetId`; two rule sets are two columns, and the view layer
   * computes the variance between any two scenario columns.
   *
   * Orçado is a *limit*; Projetada is an *expectation* — the two are never
   * interchangeable readings of the same number.
   */
  DESPESA_PROJETADA: 'DESPESA_PROJETADA',
  /**
   * Combined forward projection (ALC-2390): one scenario column filled by both
   * {@link FpaScenarioKind.RECEITA_PROJETADA} and {@link FpaScenarioKind.DESPESA_PROJETADA}.
   * The two halves are disjoint by construction (revenue-only vs expense-only
   * rollups); the composite asserts that and refuses a collision rather than
   * silently picking one. Carries `forecastRuleSetId` (required — the expense
   * half has no default) and forwards `params.assumedRenewalTerms` to the
   * revenue half (FP&A default 0). Never a revenue-only fallback: a missing
   * rule set is a refusal, not an incomplete column.
   */
  PROJECAO: 'PROJECAO',
} as const;
export type FpaScenarioKind = (typeof FpaScenarioKind)[keyof typeof FpaScenarioKind];

/**
 * One column's scenario. `kind` names the provider (ADR 0008). Budget-backed
 * scenarios (ORCADO, REFORECAST) carry `budgetId` — the plain-FK reference
 * shape agreed with slice C1; versioning itself lives in the budget module.
 * Registered future providers put their inputs in `params`.
 */
export interface FpaScenarioRef {
  kind: string;
  budgetId?: string;
  /**
   * Forecast rule set backing a rule-driven scenario (DESPESA_PROJETADA and
   * the PROJECAO composite, ALC-2116 / ALC-2390) — the same plain-FK reference
   * shape as `budgetId`, deliberately not folded into `params`: one scenario
   * kind plus a rule-set *reference* is what makes two rule sets two comparable
   * columns instead of two provider kinds.
   *
   * On PROJECAO this field is required (the expense half has no engine default).
   * `params.assumedRenewalTerms` is forwarded only to the revenue half.
   *
   * Every field of this ref is covered by the drill-down handle's HMAC, so the
   * evaluate DTO must declare it or the app-level whitelist strips it before
   * verification and every drill-down for the column fails.
   *
   * ALC-2118: Premissas and amount-level line overrides are looked up from the
   * existing `budgetId` / `forecastRuleSetId` on this ref. Do not add a parallel
   * assumption-set or override-set identifier without declaring it on the
   * evaluate DTO in the same change — the ALC-2116 whitelist/HMAC trap.
   */
  forecastRuleSetId?: string;
  params?: Record<string, unknown>;
}

// ── Forecast rule sets (ALC-2116) ────────────────────────────────────────────
// The user-authored configuration behind the DESPESA_PROJETADA scenario: a named,
// tenant-scoped, mutable set holding one or more rules, each an instance of a
// system-defined variant. Deliberately not entity/book-scoped (the column supplies
// that scope) and not budget-style versioned (edit history is the FP&A
// definition-revision journal; frozen reports pin the set they used).

/**
 * Id selector shared by a filter's include and exclude branches. Every key is a
 * list of ids and an omitted key constrains nothing; an empty list is a caller
 * error, not "none" (services reject it rather than silently matching everything).
 *
 * Both grains are first-class in v1: document-level references model "cancel this
 * contract" without touching the underlying records (story 14), dimension-level
 * ones model "freeze this area of spend" without enumerating documents (story 15).
 *
 * These are plain ids, never foreign keys in the database: a reference that stops
 * resolving — a deleted template, a renamed cost centre — must stay representable
 * so the projection can raise a coverage entry for it (story 16).
 */
export interface FpaForecastRuleSelector {
  /** Document-level: AP vendors (`ApVendor.vendorId`). */
  vendorIds?: string[];
  /** Document-level: recurring vendor bill templates (`ApVendorRecurringBill`). */
  recurringBillTemplateIds?: string[];
  /** Document-level: purchase orders (`PurchaseOrder.purchaseOrderId`). */
  purchaseOrderIds?: string[];
  /** Dimension-level: managerial accounts — the Fonte GERENCIAL attribution key. */
  managerialAccountIds?: string[];
  /** Dimension-level: GL accounts — the Fonte CONTABIL attribution key. */
  glAccountIds?: string[];
  /** Dimension-level: cost centres. */
  costCenterIds?: string[];
  /** Dimension-level: departments. */
  departmentIds?: string[];
}

/**
 * Optional narrowing of one rule's sources. An ABSENT filter (null/undefined) means
 * "all" — the two branches exist because the stories need both directions: include
 * scopes a rule to an area of spend, exclude models removing a specific commitment
 * from an otherwise unchanged projection. Applied as: a source must match `include`
 * (when present) AND match no `exclude` entry.
 */
export interface FpaForecastRuleFilter {
  include?: FpaForecastRuleSelector;
  exclude?: FpaForecastRuleSelector;
}

/**
 * Variant parameters stored on a rule (`FpaForecastRule.params`). Flat and
 * per-variant documented, following {@link FpaScenarioRef}'s `params` precedent;
 * a key belonging to another variant is a caller error, not a silent no-op.
 * `{}` means every parameter takes its variant default.
 */
export interface FpaForecastRuleParams {
  /** PAYROLL_RUNRATE only: competency-based personnel configuration. */
  payroll?: import('./fpa-payroll.js').PayrollRunRateConfigInput;
  /**
   * ADHOC_RESIDUAL only: trailing months of actual spend the residual averages
   * over (story 13 — a seasonal business needs a window matching its cycle).
   */
  trailingMonths?: number;
  /** OPEN_DOCUMENTS only: project bills that exist but are not GL-posted. */
  includeUnpostedBills?: boolean;
  /** OPEN_DOCUMENTS only: project the open remainder of purchase orders. */
  includeOpenPurchaseOrders?: boolean;
}

export interface FpaForecastRuleResponse {
  forecastRuleId: string;
  forecastRuleSetId: string;
  variant: FpaForecastRuleVariant;
  params: FpaForecastRuleParams;
  /** Null means "all sources of this variant". */
  filter?: FpaForecastRuleFilter | null;
  sortOrder: number;
  status: FpaForecastRuleStatus;
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export interface FpaForecastRuleInput {
  /**
   * Present to preserve a rule's identity across a full-list replacement (the
   * `DimensionSplitInput.splitId` precedent). Omitted for a newly authored rule.
   * Drill-down rows are labelled by their rule, so regenerating ids on every edit
   * would break the link between a projected row and the rule that produced it.
   */
  forecastRuleId?: string;
  variant: FpaForecastRuleVariant;
  params?: FpaForecastRuleParams;
  /** Omit or null for "all". */
  filter?: FpaForecastRuleFilter | null;
  sortOrder?: number;
  status?: FpaForecastRuleStatus;
}

export interface FpaForecastRuleSetResponse {
  payrollDefaultEntityId?: string | null;
  forecastRuleSetId: string;
  name: string;
  description?: string | null;
  status: FpaForecastRuleSetStatus;
  /** Number of non-deleted rules currently in the set. */
  ruleCount: number;
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export interface FpaForecastRuleSetDetailResponse extends FpaForecastRuleSetResponse {
  payrollDataRestricted?: boolean;
  rules: FpaForecastRuleResponse[];
}

/** One immutable entry in a forecast rule set's definition-revision journal. */
export interface FpaForecastRuleSetRevisionResponse {
  revisionId: string;
  action: 'CREATED' | 'UPDATED' | 'DELETED';
  changeReason?: string | null;
  changedAt: string;
}

export interface FpaCreateForecastRuleSetRequest {
  name: string;
  description?: string | null;
  rules?: FpaForecastRuleInput[];
}

export interface FpaUpdateForecastRuleSetRequest {
  /** Binds a confirmed payroll edit to its impact preview. */
  payrollImpactFingerprint?: string;
  name?: string;
  description?: string | null;
  status?: FpaForecastRuleSetStatus;
  /** Full replacement of the ordered rule list (the `FpaUpdateTemplateRequest.rows` shape). */
  rules?: FpaForecastRuleInput[];
  /** Journaled onto the definition revision (kind = FORECAST_RULE_SET). */
  changeReason?: string | null;
}

export interface FpaListForecastRuleSetsQuery {
  entityId?: string;
  status?: FpaForecastRuleSetStatus;
  includeDeleted?: boolean;
}

// ── Evaluation ───────────────────────────────────────────────────────────────

/**
 * What a coverage entry is ABOUT. The discriminant of
 * {@link FpaScenarioColumnCoverageEntry}: a revenue column annotates contracts, an
 * expense column annotates the sources a forecast rule could not carry, and one
 * `FpaColumnSet.coverage` field carries both.
 */
export type FpaScenarioCoverageSubject =
  | 'CONTRACT'
  | 'EXPENSE_SOURCE'
  | 'ASSUMPTION'
  | 'LINE_OVERRIDE'
  | 'FORMULA'
  | 'FX_CONVERSION'
  | 'RELATIVE_RANGE';

/**
 * Fields every coverage entry carries, whatever it is about — the minimum a
 * header renderer needs to say "this column is incomplete, and here is why".
 */
export interface FpaScenarioCoverageEntryBase {
  subject: FpaScenarioCoverageSubject;
  /** Null for tenant-wide annotations that belong to no single entity. */
  entityId: string | null;
  /** True when the subject still contributed some projected rows (partial exclusion). */
  projected: boolean;
  /** Engine flags — the projection is incomplete or approximated by construction. */
  flags: string[];
}

/**
 * One contract a scenario column could not project, or projected incompletely
 * (ALC-1913 / CFO-01, ALC-2114). A published forecast column must never look
 * complete while omitting contracts, so these are surfaced as column metadata
 * rather than silently dropped — and rather than refused, because the omissions
 * are by design (usage exclusion) or data-quality facts, not caller errors.
 *
 * Self-contained for header rendering: the web needs no further lookups.
 */
export interface FpaScenarioContractCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'CONTRACT';
  contractId: string;
  /** `BillingContract.code` — the human handle; null if unresolvable. */
  contractCode: string | null;
  entityId: string;
  /**
   * Why nothing (or not everything) was projected. Null when the contract did
   * project but carries exclusion flags (e.g. a metered item alongside
   * projectable ones).
   */
  skippedReason:
    'NO_START_DATE' | 'NO_TERM_END_DATE' | 'NO_EFFECTIVE_VERSION' | 'NO_PROJECTABLE_ITEMS' | null;
  /** Service windows suppressed by CANCELLED-only recognition entries (emit nothing). */
  cancelledWindows: number;
}

/** What an expense-side coverage entry names. */
export type FpaScenarioExpenseCoverageSourceKind =
  | 'RECURRING_BILL_TEMPLATE'
  | 'PURCHASE_ORDER'
  | 'BILL'
  | 'ADHOC_RESIDUAL'
  /** A filter id that resolves to no record at all. */
  | 'FILTER_REFERENCE'
  /** Payroll, which the residual carries rather than models. */
  | 'PAYROLL'
  /** One Posição in the Plano de Contratações. */
  | 'HEADCOUNT_POSITION'
  /** One roster employee in the PAYROLL_RUNRATE recurring base. */
  | 'PAYROLL_EMPLOYEE'
  /** One HcmCollaborator row (mapped or UNMAPPED_COLLABORATOR). */
  | 'PAYROLL_COLLABORATOR'
  /** The rule set itself — it resolved, but had nothing to project. */
  | 'FORECAST_RULE_SET';

/**
 * One source a DESPESA_PROJETADA column could not carry, or carried with a caveat
 * (ALC-2116). Deliberately NOT contract-shaped: an expense column annotates
 * templates, documents, filter references and modelling caveats, none of which
 * has a `contractId` or a service window.
 *
 * The four cases the ticket requires this to express:
 *
 * - a template or purchase order excluded because it is unattributable
 *   (`UNATTRIBUTABLE`) — the amount still lands in Não Classificado, so the total
 *   stays correct while the gap stays visible;
 * - a filter reference that resolves to nothing (`FILTER_REFERENCE_UNRESOLVED`) —
 *   a deleted contract or renamed cost centre must never silently shrink (or, on
 *   the exclude side, silently widen) a forecast;
 * - payroll carried at its historical run-rate rather than modelled
 *   (`PAYROLL_HISTORICAL_RUN_RATE`) — this must never be rendered as "payroll is
 *   missing", because it is not;
 * - unposted obligations held in competency months earlier than the column's
 *   first requested period (`UNPOSTED_BEFORE_RANGE`) — a Realizado/Projetada
 *   splice that starts later would silently drop them.
 */
export interface FpaScenarioExpenseCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'EXPENSE_SOURCE';
  sourceKind: FpaScenarioExpenseCoverageSourceKind;
  /** The referenced id; null for aggregate entries (payroll, before-range totals). */
  sourceId: string | null;
  /** Human handle — template name, PO number, vendor bill number, filter path. */
  sourceLabel: string | null;
  reason:
    | 'UNATTRIBUTABLE'
    | 'FILTER_REFERENCE_UNRESOLVED'
    | 'PAYROLL_HISTORICAL_RUN_RATE'
    | 'UNPOSTED_BEFORE_RANGE'
    | 'NO_COMPETENCY_DATE'
    | 'NO_AMOUNT'
    | 'OCCURRENCE_CAP_REACHED'
    /**
     * A template or PO with NO live obligation left in the column (recurrence ended, PO fully
     * billed) had its posted history swept into the ad-hoc run-rate instead of being suppressed.
     * The spend is still in the column — just under `ADHOC_RESIDUAL` rather than under its own
     * name, which is why the residual moved (Q-06).
     */
    | 'ENDED_SOURCE_IN_RESIDUAL'
    /**
     * The rule set carries no ACTIVE rule — it was emptied, or soft-deleted (which
     * soft-deletes its rules). The column is legitimately zero, and saying so is the
     * difference between "we spend nothing" and "nothing was modelled".
     */
    | 'NO_PROJECTABLE_RULES'
    /**
     * HEADCOUNT_PLAN is active, so payroll history is excluded from the residual
     * (ALC-2117). Distinct from PAYROLL_HISTORICAL_RUN_RATE.
     */
    | 'PAYROLL_EXCLUDED_BY_HEADCOUNT_PLAN'
    /**
     * HEADCOUNT_PLAN emitted unattributed rows (null GL/managerial). Placement
     * in Não Classificado is by design until ALC-2132; not a tenant data error.
     */
    | 'HEADCOUNT_PLAN_UNATTRIBUTED'
    /**
     * A non-BR entity is in the evaluate scope. BR statutory encargos are not
     * applied; the entity's positions are omitted. Distinct from a request-level
     * 400 on the single-entity report path.
     */
    | 'ENCARGOS_JURISDICTION_UNSUPPORTED'
    /**
     * The entity has its own dated `byEntity` encargos policy configured, but
     * none of its windows cover the requested period (ALC-2545). Positions
     * are omitted; never silently downgraded to the tenant default.
     */
    | 'ENCARGOS_POLICY_NOT_EFFECTIVE'
    /** Last MONTHLY roster minus TERMINATION CPFs. */
    | 'PAYROLL_ROSTER'
    /** Future joiners are not derived from payroll. */
    | 'JOINERS_NOT_MODELLED'
    /** Tenant provision branch the projection followed (INLINE vs IMPORTED). */
    | 'PAYROLL_PROVISION_BRANCH'
    /** Unrecognised rubrics are excluded until classified. */
    | 'UNRECOGNISED_RUBRIC'
    /** Org-unit GL hop did not resolve a managerial account — Fonte CONTABIL only. */
    | 'PAYROLL_MANAGERIAL_UNRESOLVED'
    /** Employee (or PJ attribution) dropped by the rule filter. */
    | 'PAYROLL_FILTER_EXCLUDED'
    /** HcmCollaborator row has neither partyId nor vendorId. */
    | 'UNMAPPED_COLLABORATOR'
    /** PAYROLL_RUNRATE is active, so payroll-explained spend left the residual. */
    | 'PAYROLL_EXCLUDED_BY_PAYROLL_RUNRATE'
    /** Last MONTHLY (or pró-labore) run is older than the recency window. */
    | 'PAYROLL_BASE_RUN_STALE'
    /** Employee has a TERMINATION run but no CPF — identity is ambiguous. */
    | 'PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS'
    /** CLT run or collaborator bill currency differs from the book's functional currency — refuse, never convert. */
    | 'PAYROLL_CURRENCY_MISMATCH'
    /** IMPORTED-branch employee absent from the latest provision runs. */
    | 'PAYROLL_PROVISION_MISSING'
    /** Roster employee skipped because the computed amount was zero or negative. */
    | 'PAYROLL_EMPLOYEE_SKIPPED'
    | 'INSUFFICIENT_HISTORY'
    | 'NO_SOURCE_DATA'
    | 'UNAVAILABLE'
    /** Collaborator has no bill inside the trailing window. */
    | 'PAYROLL_COLLABORATOR_STALE'
    /** Mapped collaborator produced no projectable row (materialized template, empty walk). */
    | 'PAYROLL_COLLABORATOR_NO_EMISSION'
    /**
     * PAYROLL_RUNRATE refused the entity (incomplete imported provision) and
     * ADHOC_RESIDUAL is carrying that entity's posted payroll history.
     */
    | 'PAYROLL_REFUSED_COVERED_BY_RESIDUAL'
    /**
     * A PAYROLL template's occurrence left RECURRING_BILLS on Natureza alone (ALC-1902 CFO-1),
     * but no payroll lane in the active rule set actually modelled it. Never a silent drop.
     */
    | 'PAYROLL_TEMPLATE_NOT_MODELLED'
    /**
     * A document links to a PAYROLL template, but its competency month sits outside that
     * template's CURRENT vigência (ALC-1902 CFO-2) — the template was edited after the fact.
     * Classification is kept stable; this names the drift instead of a silent reclassification.
     */
    | 'PAYROLL_TEMPLATE_WINDOW_DRIFTED';
  /** The forecast rule this entry belongs to; null when it is not rule-specific. */
  ruleId: string | null;
  /** `UNPOSTED_BEFORE_RANGE` only: the earliest competency month held, `YYYY-MM-01`. */
  earliestCompetencyMonth: string | null;
  /** How many units the entry stands for (obligations, unattributable rows, …). */
  count: number;
}

/**
 * A scenario column's incompleteness annotation, discriminated on `subject`.
 *
 * One field carries both shapes because coverage is a property of the COLUMN, not
 * of the provider behind it: the renderer must be able to warn on any scenario
 * column without knowing which engine produced it.
 */
/**
 * An `assumption_ref` that could not resolve for this column (ALC-2118).
 * Named so a blank cell is never a silent zero: the key is on the column.
 */
export interface FpaScenarioAssumptionCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'ASSUMPTION';
  assumptionKey: string;
  /** Null for column-level reasons that are not a single month. */
  period: FpaPeriodKey | null;
  reason: 'UNRESOLVED' | 'REALIZADO_HAS_NO_PREMISSA' | 'SCENARIO_KIND_INCONSISTENT';
}

/**
 * A line override that was not applied to this column (ALC-2118).
 * Currency mismatch is skipped (never report-fatal); an AGGREGATION pin is
 * skipped because a hardcoded subtotal cannot reconcile with its detail rows.
 * A CALCULATED pin has no account population, so it cannot flow into
 * aggregated subtotals (`CALCULATED_PIN_SKIPPED`). A null/refused
 * aggregation cell stays null (`NULL_CELL_SKIPPED`) instead of becoming
 * `0 + delta`. A non-zero pin that no aggregation contains is
 * `PIN_NOT_CONTAINED`. Membership that cannot be decided because an
 * explicit account is missing from the scoped catalog is
 * `PIN_ACCOUNTS_UNMATERIALIZED` (emitted whenever any explicit id fails to
 * materialize, even if another ancestor absorbed the delta). Formula
 * operands of a pinned union or alias are not adjusted when they would
 * otherwise have contained (`OPERAND_NOT_ADJUSTED`). When every containing
 * candidate was an operand, the pin is `OPERANDS_ONLY` rather than
 * `PIN_NOT_CONTAINED`.
 */
export interface FpaScenarioLineOverrideCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'LINE_OVERRIDE';
  overrideId: string;
  rollupId: string;
  reason:
    | 'CURRENCY_MISMATCH'
    | 'AGGREGATION_PIN_SKIPPED'
    | 'CALCULATED_PIN_SKIPPED'
    | 'NULL_CELL_SKIPPED'
    | 'PIN_NOT_CONTAINED'
    | 'PIN_ACCOUNTS_UNMATERIALIZED'
    | 'OPERAND_NOT_ADJUSTED'
    | 'OPERANDS_ONLY';
  /** Present on `CURRENCY_MISMATCH`. */
  stampedCurrency?: string | null;
  /** Present on `CURRENCY_MISMATCH`. */
  evaluationCurrency?: string | null;
  /** Present on `PIN_ACCOUNTS_UNMATERIALIZED`. */
  accountIds?: string[];
}

/**
 * A formula cell omitted because `prior_period` needs a month the column's
 * provider grid does not carry (blank-is-not-zero).
 */
export interface FpaScenarioFormulaCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'FORMULA';
  rollupId: string;
  period: FpaPeriodKey;
  reason: 'PRIOR_PERIOD_OUTSIDE_COLUMN';
}

/**
 * One source currency a projection column converted into the evaluation scope's
 * functional currency (ALC-2417).
 *
 * Projection columns used to refuse a mixed-currency scope outright. They now
 * convert at the PTAX closing (venda) bulletin — but a converted forecast must
 * NEVER read as if it had been stated in the target currency all along, so every
 * conversion emits one of these and the column header annotates it.
 *
 * Self-contained for rendering AND for audit: an accounting reviewer can re-derive
 * every converted amount from `exactRate`, and trace the rate itself back to a
 * specific BCB bulletin through `quoteDate` / `quoteIdentifier`.
 *
 * Scope of the rate (deliberate, and the reason `requestedDate` is separate from
 * `quoteDate`): projections are FUTURE months, for which no bulletin exists. ONE
 * rate — the latest available as of the evaluation moment — is applied to every
 * projected month. This is explicitly not a per-month forward curve, and the two
 * dates make that visible: `requestedDate` is when the report was run,
 * `quoteDate` is the bulletin that answered.
 */
export interface FpaScenarioFxConversionCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'FX_CONVERSION';
  /** The projected rows' own currency. */
  fromCurrency: string;
  /** The evaluation scope's functional currency — never a literal. */
  toCurrency: string;
  /** Units of `toCurrency` per one unit of `fromCurrency`, 6dp — the display rate. */
  rate: string;
  /** The 18dp rate the amounts were actually multiplied by. Audit re-derivation uses THIS. */
  exactRate: string;
  /** ISO date the rate was requested for: the evaluation moment. */
  requestedDate: string;
  /** ISO date of the bulletin actually used, after prior-business-day fallback. */
  quoteDate: string;
  source: 'IDENTITY' | 'PTAX' | 'DOF' | 'FED_H10';
  provider: 'SYSTEM' | 'BCB_PTAX' | 'BANXICO_DOF' | 'FRB_H10';
  /** Deterministic provider/bulletin coordinates — immutable quote identity. */
  quoteIdentifier: string;
  /** Single-valued today; kept as a field so the renderer stays branch-free. */
  reason: 'CONVERTED_AT_PTAX';
  /** How many projected rows were converted at this rate. */
  count: number;
}

/**
 * The FX receipt a converted projection cell carries on its drill-down handle
 * (ALC-2624).
 *
 * ## The bug this closes
 *
 * A converted projection cell resolves its PTAX rate at EVALUATION time. The
 * drill-down used to fix a new `now` and re-resolve — so a bulletin published
 * (or a UTC midnight crossed) between rendering the report and clicking the cell
 * gave the decomposition a different rate. The rows and their reconciling total
 * then disagreed with the number on screen, silently, with no way for the user
 * to tell which of the two was the "real" figure.
 *
 * ## Why the rate travels in the handle rather than being looked up again
 *
 * The handle is HMAC-signed over every field, so the pin is a *server-issued
 * receipt*: a client cannot inject or edit a rate, and there is no second lookup
 * that could answer differently the second time. It is deliberately the whole
 * quote — not just the multiplicand — so the decomposition is auditable on its
 * own terms (`quoteIdentifier` + `quoteDate` trace back to one BCB bulletin) and
 * so a pinned decomposition reproduces the column's `FX_CONVERSION` coverage
 * entry field-for-field instead of approximating it.
 *
 * Structurally a projection of {@link FpaScenarioFxConversionCoverageEntry}: the
 * pin IS the coverage annotation minus the presentation fields, which is what
 * lets the evaluator mint it by picking off the coverage the column already
 * published, with no parallel derivation that could drift.
 */
export type FpaCellDrilldownFxPin = Pick<
  FpaScenarioFxConversionCoverageEntry,
  | 'fromCurrency'
  | 'toCurrency'
  | 'rate'
  | 'exactRate'
  | 'requestedDate'
  | 'quoteDate'
  | 'source'
  | 'provider'
  | 'quoteIdentifier'
>;

export type FpaScenarioColumnCoverageEntry =
  | FpaScenarioContractCoverageEntry
  | FpaScenarioExpenseCoverageEntry
  | FpaScenarioAssumptionCoverageEntry
  | FpaScenarioLineOverrideCoverageEntry
  | FpaScenarioFormulaCoverageEntry
  | FpaScenarioFxConversionCoverageEntry
  | FpaRelativeRangeCoverageEntry;

export type FpaColumnAnchor = 'LAST_CLOSED_MONTH' | 'CURRENT_MONTH';

export interface FpaRelativeRange {
  anchor: FpaColumnAnchor;
  /** Months from the anchor to the first column month (may be negative). */
  fromOffset: number;
  /** Months from the anchor to the last column month; greater than or equal to fromOffset. */
  toOffset: number;
}

/**
 * Response-only explanation for a LAST_CLOSED_MONTH range that had no closed
 * accounting period to anchor to. The live report used the previous calendar
 * month instead.
 *
 * Live evaluates only. A relative range is a property of a template's
 * `defaultColumns`, and a snapshot does NOT capture `defaultColumns` — it freezes
 * its own explicit period axis, resolved once at freeze time. So a snapshot read
 * carries neither `relativeRange` nor this annotation: there is no rolling range
 * left to re-resolve, and nothing for the fallback to explain.
 */
export interface FpaRelativeRangeCoverageEntry extends FpaScenarioCoverageEntryBase {
  subject: 'RELATIVE_RANGE';
  entityId: null;
  projected: true;
  flags: ['NO_CLOSED_PERIOD_FALLBACK'];
  reason: 'NO_CLOSED_PERIOD';
  anchor: 'LAST_CLOSED_MONTH';
  resolvedAnchor: FpaPeriodKey;
  fallback: 'PREVIOUS_CALENDAR_MONTH';
}

/**
 * Response-only per-(subject, reason) aggregate for bulk coverage that did not
 * make it into {@link FpaColumnSet.coverage} as individual entries (ALC-2699).
 *
 * A DESPESA_PROJETADA/PROJECAO column can annotate one entry per (rollup ×
 * period) gap — thousands on a sparse tenant — and every one of those entries
 * says the same thing: "this reason, again". Returning them one-by-one bloats
 * the response without adding information a reader can act on beyond "there
 * are `count` of these, for `reason`". This type carries exactly that, plus a
 * bounded, inspectable `sample` of the real entries so the popover still shows
 * something concrete rather than only a number.
 *
 * Disclosure classes (RELATIVE_RANGE, FX_CONVERSION, LINE_OVERRIDE,
 * ASSUMPTION) are never summarized this way — each one says the engine
 * changed or withheld a number, so every one of them always rides in
 * `coverage` as an individual entry (see {@link FpaColumnSet.coverage}).
 *
 * `kind` is the discriminant a renderer switches on before reading a coverage
 * entry's own `subject`-based union: it is a literal that no
 * {@link FpaScenarioCoverageSubject} value can ever equal, so a summary can
 * never be mistaken for (or narrowed as) a granular entry sharing its
 * `subject`.
 */
export interface FpaScenarioCoverageSummaryEntry {
  kind: 'COVERAGE_SUMMARY';
  /** The subject every aggregated entry shares. */
  subject: FpaScenarioCoverageSubject;
  /**
   * The reason code every aggregated entry shares. For `CONTRACT` this is
   * `skippedReason ?? 'PARTIAL'` (a projected-but-partial contract has no
   * `skippedReason`); every other subject groups on its own `reason` field
   * verbatim, so the same `report.coverage.reasons.*` i18n keys that already
   * label individual entries label a summary too.
   */
  reason: string;
  /** True count of entries this summary stands for. */
  count: number;
  /**
   * Bounded prefix of the real entries, in the same stable order the full set
   * would have used. Never the whole group — that is what `count` is for.
   */
  sample: FpaScenarioColumnCoverageEntry[];
}

/** One report column request: a scenario evaluated over an ordered period list. */
export interface FpaColumnSet {
  scenarioRef: FpaScenarioRef;
  periods: FpaPeriodKey[];
  /**
   * Persisted rolling-range definition. On template reads, `periods` contains
   * the server-resolved concrete months alongside this original definition.
   */
  relativeRange?: FpaRelativeRange;
  /**
   * Display label for the column group. Budget-backed columns are labeled by the
   * server with the version name (disambiguated by version ordinal, ALC-1907) so
   * two versions of one budget never render identical headers; absent for
   * Realizado, where the client falls back to its own scenario-kind label.
   */
  label?: string;
  /**
   * Response-only column annotation (like `label`, server-written): contracts this
   * scenario column omitted or projected incompletely. Absent when coverage is
   * complete. Never signed into drill-down handles (the HMAC binds `scenarioRef`
   * only).
   *
   * MAY BE TRUNCATED, and MAY BE EMPTY WHILE {@link coverageSummaries} IS NOT
   * (ALC-2699): when the pre-cap count exceeded the response cap, bulk
   * (non-disclosure) entries are aggregated into `coverageSummaries` instead of
   * riding here individually, so `coverage` can legitimately be `[]` — never
   * absent — while real caveats still exist. Disclosure classes (RELATIVE_RANGE,
   * FX_CONVERSION, LINE_OVERRIDE, ASSUMPTION) are the only entries that ever
   * appear here once truncation happens; they are never aggregated, and they
   * always lead. {@link coverageTotal} states the true pre-aggregation count.
   */
  coverage?: FpaScenarioColumnCoverageEntry[];
  /**
   * Response-only. Present exactly when the response cap collapsed bulk
   * (non-disclosure) coverage into per-(subject, reason) summaries instead of
   * dropping or individually listing it (ALC-2699). Absent — never an empty
   * array — when nothing was aggregated, including on every response from
   * before this field existed and on every frozen snapshot column (snapshots
   * freeze uncapped coverage straight off the evaluator and never carry this
   * field at all).
   */
  coverageSummaries?: FpaScenarioCoverageSummaryEntry[];
  /**
   * Response-only. The true pre-cap coverage count — the number of individual
   * entries `coverage` would have carried with no cap, i.e. `coverage.length`
   * plus every entry folded into `coverageSummaries` — present ONLY when the
   * response was truncated. Its absence means `coverage.length` is the whole
   * story. Renderers must show "{coverage.length} of {coverageTotal}" (or, once
   * `coverageSummaries` is present, the aggregated equivalent) rather than a
   * bare count, so a capped column never asserts a number of caveats it does
   * not have.
   *
   * Live evaluates only: snapshots freeze uncapped coverage (they call the
   * evaluator directly), so a frozen column never carries this field even when the
   * equivalent live column would.
   */
  coverageTotal?: number;
}

/**
 * Template-write shape. Persisted default columns carry exactly one of
 * `periods` or `relativeRange`; template reads materialize a regular
 * {@link FpaColumnSet} with concrete periods.
 *
 * Deliberately an ALLOWLIST (`Pick`) rather than an `Omit` denylist: response-only
 * fields keep being added to {@link FpaColumnSet} (`coverage`, `coverageTotal`),
 * and under an `Omit` each new one would silently become writable through the
 * template-save path. Adding a persistable field here is now an explicit decision.
 */
export interface FpaDefaultColumnInput extends Pick<FpaColumnSet, 'scenarioRef' | 'label'> {
  periods?: FpaPeriodKey[];
  relativeRange?: FpaRelativeRange;
}

/**
 * Opaque handle a client passes back to the drill-down endpoint to decompose a
 * cell. Serializable; carries everything needed to re-derive the cell's rows.
 */
export interface FpaCellDrilldownHandle {
  templateId: string;
  /** Frozen template regime, covered by the server-side signature. */
  regime: FpaRegime;
  rowId: string;
  scenarioRef: FpaScenarioRef;
  period: FpaPeriodKey;
  /**
   * The evaluating column's full period set (signed allowlist). Drill-down
   * resolves providers over exactly these periods so `prior_period` matches
   * evaluate on a multi-month column.
   */
  columnPeriods: FpaPeriodKey[];
  entityIds: string[];
  /**
   * Ledger book the cell was evaluated against (ALC-1905). Always echoed back by the
   * evaluator: the book filter is mandatory, and without it a drill-down could
   * re-derive the cell against a different book and fail to reconcile.
   */
  bookId?: string | null;
  /**
   * The exact FX quotes the evaluating column converted with (ALC-2624), or
   * absent/null when the column resolved no FX at all.
   *
   * Three states, and the difference between the last two is load-bearing:
   *
   * - **Absent / null** — this column never ran FX resolution (Realizado, budget
   *   versions, Caixa, and snapshot re-mints). Drill-down behaves exactly as it
   *   did before this field existed.
   * - **`[]`** — the column DID resolve FX and converted nothing (a
   *   single-currency projection scope). Drill-down is pinned to "no conversion":
   *   if the scope has since grown a foreign row, the decomposition refuses
   *   (`FPA_DRILLDOWN_FX_PIN_MISSING`) instead of converting at today's rate and
   *   adding an amount the cell never contained.
   * - **Non-empty** — one receipt per source currency the column converted.
   *   Drill-down MUST reuse these rates and MUST NOT re-resolve.
   *
   * Signed like every other field: a client cannot add, drop or edit a rate.
   */
  fx?: FpaCellDrilldownFxPin[] | null;
  /**
   * Binds this handle to ONE frozen snapshot artifact (ALC-2608 follow-up).
   * Absent for a live-evaluate handle. A snapshot-response handle always
   * carries it — two snapshots of the same template otherwise share every
   * other field (templateId, scenarioRef, entityIds, bookId), so without this
   * a handle re-minted for one snapshot verifies identically against ANY
   * other same-template snapshot and can be used to fetch ITS frozen
   * composition instead. Signed like every other field.
   */
  snapshotId?: string;
  /** Base64url HMAC over this handle's scope plus the caller tenant. */
  /** Signed reporting translation and entity-book scope, reused without fresh FX lookups. */
  reporting?: FpaReportingDisclosure;
  signature: string;
}

/** Combined management presentation; no intercompany eliminations or ledger restatement. */
export interface FpaReportingDisclosure {
  currency: string;
  policy: 'EVENT_DATE_PTAX';
  /** Actuals use event-date PTAX; monthly metrics use month-end, capped at this date. */
  asOfDate: string;
  entities: { entityId: string; bookId: string; functionalCurrency: string }[];
  rates: (FpaCellDrilldownFxPin & { period: FpaPeriodKey })[];
}

export interface FpaEvaluatedCell {
  /** Decimal serialized as string to preserve precision. */
  value: string;
  /** Present only for rows whose display format is CURRENCY. */
  currency?: string;
  drilldown: FpaCellDrilldownHandle;
}

export interface FpaEvaluatedRow {
  rowId: string;
  rowType: FpaTemplateRowType;
  rollupId?: string | null;
  label?: string | null;
  format?: FpaDisplayFormat | null;
  bold: boolean;
  indentLevel: number;
  signConvention: FpaSignConvention;
  /** One cell per (column, period), keyed `${columnIndex}:${period}`. */
  cells: Record<string, FpaEvaluatedCell>;
}

/**
 * Explicit unattributable buckets (ADR 0007): amounts no rollup selected
 * (naoClassificado) and, under Regime CAIXA, competência amounts with no
 * settlement to attribute (semEventoDeCaixa). Same cell shape as ordinary rows
 * so they render and drill down uniformly.
 */
export interface FpaBucketRow {
  bucket: 'naoClassificado' | 'semEventoDeCaixa';
  cells: Record<string, FpaEvaluatedCell>;
}

export interface FpaEvaluatedReport {
  templateId: string;
  /**
   * Echo of the template's evaluation semantics, so the payload — and any ADR-0009
   * snapshot embedding it — is self-describing (ADR 0007 requires the active date
   * basis in report metadata).
   */
  regime: FpaRegime;
  fonte: FpaFonte;
  /** Null for Regime Caixa, which has no Base de Data. */
  dateBasis: FpaDateBasis | null;
  columns: FpaColumnSet[];
  /** Multi-select entity scope; straight sum, "combinado (sem eliminações)". */
  entityIds: string[];
  rows: FpaEvaluatedRow[];
  reporting?: FpaReportingDisclosure;
  buckets: FpaBucketRow[];
}

// ── Drill-down ───────────────────────────────────────────────────────────────

/**
 * Which date landed a drill-down row in its period. Under `dateBasis = GERENCIAL` a row
 * is re-dated from its source document, so the answer is not always the journal date
 * (ADR 0007 requires the basis to be visible on the row).
 */
export type FpaDrilldownDateSource =
  'JOURNAL' | 'BILL_MANAGERIAL' | 'BILL_ISSUE' | 'INVOICE_MANAGERIAL' | 'INVOICE_ISSUE';

/** Backend-owned ordering supported by the cell-composition listing. */
export type FpaDrilldownSortBy = 'date' | 'vendor' | 'amount';
export type FpaDrilldownSortDirection = 'asc' | 'desc';

/** Query state for the paged cell-composition listing. */
export interface FpaDrilldownQuery {
  page?: number;
  limit?: number;
  sortBy?: FpaDrilldownSortBy;
  sortDirection?: FpaDrilldownSortDirection;
  vendorId?: string;
  vendorIds?: string[];
  vendor?: string;
  customerId?: string;
  customerIds?: string[];
  customer?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: string;
  amountMax?: string;
}

export type FpaDrilldownRow =
  | {
      type: 'journal_line';
      journalLineId: string;
      journalHeaderId: string;
      glAccountId: string;
      vendorId?: string | null;
      vendorName?: string | null;
      billId?: string | null;
      invoiceId?: string | null;
      customerId?: string | null;
      customerName?: string | null;
      description?: string | null;
      amount: string;
      currency: string;
      date: string;
      dateSource?: FpaDrilldownDateSource;
      period?: FpaPeriodKey;
    }
  | {
      /**
       * A managerial split as it was materialised onto the ledger at posting time.
       * `classificationId` and `managerialAccountId` are nullable because the Não
       * Classificado population is real: managerial enforcement is approval-time and
       * tenant-configurable, and AR's legacy path posts revenue from
       * `revenueGlAccountId` with no managerial account at all (ALC-1905).
       */
      type: 'classification_split';
      classificationId: string | null;
      journalLineId?: string | null;
      billId?: string | null;
      managerialAccountId: string | null;
      vendorId?: string | null;
      vendorName?: string | null;
      invoiceId?: string | null;
      customerId?: string | null;
      customerName?: string | null;
      description?: string | null;
      amount: string;
      currency: string;
      date: string;
      dateSource?: FpaDrilldownDateSource;
      period?: FpaPeriodKey;
    }
  | {
      type: 'settlement_allocation';
      settlementId: string;
      /** Accrual journal line whose dimension owns this cash-attributed slice. */
      journalLineId: string;
      sourceDocumentId: string;
      amount: string;
      currency: string;
      settledAt: string;
    }
  | {
      type: 'budget_line';
      budgetId: string;
      budgetLineId: string;
      period: FpaPeriodKey;
      amount: string;
      currency: string;
    }
  | {
      type: 'metric_point';
      seriesId: string;
      metricKey: string;
      period: FpaPeriodKey;
      value: string;
    }
  | {
      /**
       * One forward-revenue engine row (ALC-1913): a competency month × contract
       * item × classification split group, from the RECEITA_PROJETADA scenario.
       * `provenance` distinguishes subledger facts (RECOGNIZED/SCHEDULED) from
       * the engine's assumptions (PROJECTED_TERM/PROJECTED_RENEWAL) — a consumer
       * must never present the latter as the former.
       */
      type: 'projected_revenue';
      contractId: string;
      contractItemId: string | null;
      serviceId: string | null;
      provenance: 'RECOGNIZED' | 'SCHEDULED' | 'PROJECTED_TERM' | 'PROJECTED_RENEWAL';
      period: FpaPeriodKey;
      amount: string;
      currency: string;
      /** The same id the GL journal carries, so a row traces to its journal line. */
      splitId: string | null;
      /** 0 in-term, 1..N for assumed renewal terms. */
      renewalOrdinal: number;
      /** Engine row flags (e.g. CATCHUP_ABSORBED, PRORATED_FIRST_CYCLE). */
      flags: string[];
      serviceWindowStart: string;
      serviceWindowEnd: string;
    }
  | {
      /**
       * The "PTAX dinâmica" remeasurement delta for one RECOGNIZED/USAGE_ASSET
       * origin (ALC-2625, `params.dynamicPtax` on RECEITA_PROJETADA/PROJECAO):
       * today's PTAX conversion of the origin minus its booked value. Always
       * paired with a sibling `projected_revenue` row for the SAME origin
       * (matched by contractId/contractItemId/serviceId/period/splitId), which
       * carries the booked value unchanged — the pair together foot to what the
       * remeasurement moved the origin to, so the revenue line still reconciles
       * to the book. Never emitted when the option is off, when the row is
       * settled (`ADVANCE_LIABILITY`), or when the currencies already matched
       * (nothing to remeasure).
       */
      type: 'projected_revenue_fx_remeasurement';
      contractId: string;
      contractItemId: string | null;
      serviceId: string | null;
      period: FpaPeriodKey;
      /** Signed: remeasured − booked. */
      amount: string;
      currency: string;
      splitId: string | null;
      /** The origin row's own currency — what was remeasured FROM. */
      fromCurrency: string;
      /** The column's currency — what was remeasured TO (same as `currency`). */
      toCurrency: string;
    }
  | {
      /**
       * One forward-expense engine row (ALC-2116): a competency month × source ×
       * classification split group, from the DESPESA_PROJETADA scenario.
       * `provenance` separates a commitment that already exists as a document
       * from an occurrence walked forward from a recurring template, and both
       * from the historical run-rate — a consumer must never present an
       * assumption as a commitment (story 23).
       *
       * Every row links its source document EXCEPT the ad-hoc residual, which has
       * none: it is one unlinked row per rule per cell, labelled by its rule and
       * trailing window. That is a deliberate departure from every other
       * drill-down row in the system, which all link a document.
       */
      type: 'projected_expense';
      provenance:
        | 'EXISTING_DOCUMENT'
        | 'RECURRING_OCCURRENCE'
        | 'HISTORICAL_AVERAGE'
        | 'HEADCOUNT_PLAN'
        | 'PAYROLL_RUNRATE';
      period: FpaPeriodKey;
      amount: string;
      currency: string;
      /** `FpaForecastRule.forecastRuleId` — which rule of the set produced the row. */
      ruleId: string;
      variant: FpaForecastRuleVariant;
      sourceKind:
        | 'BILL'
        | 'PURCHASE_ORDER'
        | 'RECURRING_BILL_TEMPLATE'
        | 'ADHOC_RESIDUAL'
        | 'HEADCOUNT_POSITION'
        | 'PAYROLL_EMPLOYEE'
        | 'PAYROLL_COLLABORATOR';
      /** Null ONLY for `ADHOC_RESIDUAL`. */
      sourceId: string | null;
      /** Template name, PO number, vendor bill number, or the residual's window. */
      sourceLabel: string | null;
      vendorId: string | null;
      /** The same id the source document's classification carries, when there is one. */
      splitId: string | null;
      /** Engine row flags (e.g. AMOUNT_ESTIMATED, PAYROLL_RUN_RATE). */
      flags: string[];
    }
  | {
      /** Formula cells decompose one level into their operands. */
      type: 'formula_operand';
      operand: FpaFormulaNode;
      value: string;
      currency?: string | null;
      /**
       * Display format of a non-monetary operand (a PERCENT/NUMBER
       * `rollup_ref`); absent for monetary operands, which carry `currency`.
       */
      format?: FpaDisplayFormat | null;
      drilldown?: FpaCellDrilldownHandle | null;
      /**
       * Present when `operand` is `assumption_ref` (ALC-2118): names the
       * Premissa that produced this operand so a derived cell stays explainable.
       */
      assumption?: {
        assumptionKey: string;
        name: string;
        unit: string;
        period: FpaPeriodKey;
      } | null;
      /** Present when this metric operand was changed through an approved restatement. */
      restatement?: {
        restated: true;
        reason: string;
        requesterId: string;
        approverId: string;
      } | null;
      /**
       * Latest approved restatement for every entity series contributing to a
       * combined metric operand. `restatement` remains for single-series
       * consumers; this collection prevents multi-series attribution loss.
       */
      restatements?:
        | {
            seriesId: string;
            restated: true;
            reason: string;
            requesterId: string;
            approverId: string;
          }[]
        | null;
    }
  | {
      /**
       * The pin that replaced a CALCULATED cell (ALC-2118).
       * Rows + this override reconcile to the post-override cell.
       */
      type: 'line_override';
      overrideId: string;
      amount: string;
      /** Present only when the pinned rollup's display format is CURRENCY. */
      currency?: string;
      /** Display format of a pin on a non-monetary (PERCENT/NUMBER) rollup. */
      format?: FpaDisplayFormat | null;
      effectiveFrom: FpaPeriodKey;
      effectiveTo?: FpaPeriodKey | null;
      provenance?: string | null;
    }
  | {
      /**
       * Delta applied to an AGGREGATION ancestor so a pinned descendant still
       * foots the subtotal (ALC-2118). Amount is pin − derived child value.
       */
      type: 'override_adjustment';
      overrideId: string;
      childRollupId: string;
      amount: string;
      currency: string;
      provenance?: string | null;
    };

export interface FpaDrilldownResult {
  handle: FpaCellDrilldownHandle;
  rows: FpaDrilldownRow[];
  /** Sum of rows; must equal the cell value it decomposes. */
  reconcilingTotal: string;
  /** Display format of the decomposed cell; absent = CURRENCY. */
  format?: FpaDisplayFormat | null;
  /** Present only for CURRENCY-format cells — a percent/number total has no currency. */
  currency?: string;
  /** Present for the paged composition listing; the reconciling total stays unfiltered. */
  pagination?: {
    page: number;
    limit: number;
    total: number;
  };
  /**
   * The formula that produced a CALCULATED cell (ALC-2118). Absent for
   * aggregation / bucket decompositions.
   */
  formula?: FpaFormulaNode | null;
}

// ── Metrics ──────────────────────────────────────────────────────────────────

export interface FpaMetricSeriesResponse {
  seriesId: string;
  metricKey: string;
  name?: string | null;
  description?: string | null;
  unit?: string | null;
  entityId?: string | null;
  producer: FpaMetricProducer;
  currencyCode?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface FpaMetricPointResponse {
  seriesId: string;
  period: FpaPeriodKey;
  value: string;
  /** SYSTEM computation timestamp; null for tenant-authored points. */
  computedAt?: string | null;
  /**
   * Currency of the owning series (null for unitless metrics). Populated by
   * the metric provider so formula evaluation never sums distinct currencies;
   * plain series/point listings omit it (the series carries it).
   */
  currencyCode?: string | null;
  /** Unit of measure of the owning series, populated by the metric provider. */
  unit?: string | null;
}

export interface FpaUpsertMetricSeriesRequest {
  metricKey: string;
  name?: string | null;
  description?: string | null;
  unit?: string | null;
  entityId?: string | null;
  currencyCode?: string | null;
  points?: { period: FpaPeriodKey; value: string }[];
}

export interface FpaListMetricSeriesQuery {
  metricKey?: string;
  entityId?: string;
  producer?: FpaMetricProducer;
}

export interface FpaMetricSeriesDetailResponse extends FpaMetricSeriesResponse {
  /** All points of the series ordered by period ascending. */
  points: FpaMetricPointResponse[];
}

export interface FpaListMetricPointsQuery {
  /** Inclusive `YYYY-MM` lower bound. */
  from?: FpaPeriodKey;
  /** Inclusive `YYYY-MM` upper bound. */
  to?: FpaPeriodKey;
}

export interface FpaRequestMetricRestatementRequest {
  period: FpaPeriodKey;
  /** New decimal-string value; null or omitted requests point deletion. */
  value?: string | null;
  reason: string;
}

export interface FpaMetricRestatementResponse {
  restatementId: string;
  seriesId: string;
  metricKey: string;
  entityId: string | null;
  period: FpaPeriodKey;
  previousValue: string | null;
  newValue: string | null;
  reason: string;
  protections: string[];
  status: 'PENDING' | 'APPROVED';
  requestedBy: string;
  requestedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
}

/** Materializes the SYSTEM billing metrics catalog for the given periods. */
export interface FpaMaterializeBillingMetricsRequest {
  periods: FpaPeriodKey[];
}

/** Reserved response shape for a future approved metric-restatement workflow. */
export interface FpaRestatedMetricPoint {
  period: FpaPeriodKey;
  metricKey: string;
  entityId: string | null;
  /** null when the protected-period point did not previously exist. */
  previousValue: string | null;
  /** null when re-materialization removed the point. */
  newValue: string | null;
}

export interface FpaMaterializeBillingMetricsResponse {
  periods: FpaPeriodKey[];
  seriesUpserted: number;
  pointsUpserted: number;
  /** Stale billing points removed for re-materialized periods. */
  pointsDeleted: number;
  /** Computation timestamp labeling the materialized points. */
  computedAt: string;
  /**
   * Reserved for a future maker-checker restatement workflow. Ordinary
   * materialization rejects protected-period changes, so this is empty today.
   */
  restatedPoints: FpaRestatedMetricPoint[];
}

/**
 * Drill-down below a metric point. TENANT points are their own source (both
 * lists empty); SYSTEM billing points reference the contract items (snapshot
 * metrics) or classified movements (movement metrics) that sum to the value.
 */
export interface FpaMetricPointSourcesResponse {
  seriesId: string;
  metricKey: string;
  period: FpaPeriodKey;
  producer: FpaMetricProducer;
  value: string;
  billingSources: FpaMetricContractSource[];
  billingMovements: FpaMetricContractSource[];
}

/** Lean contract-level evidence for a billing metric point. */
export interface FpaMetricContractSource {
  contractId: string;
  contractCode: string;
  /** The contract represented by this evidence row; always exactly one id. */
  contractIds: string[];
  customerId: string;
  amount: string;
  period: FpaPeriodKey;
}

/** A standard template resolved while seeding a single FP&A fonte. */
export interface FpaStandardContentTemplate {
  fonte: FpaFonte;
  templateId: string;
  name: string;
}

/** Outcome of seeding the standard rollups and the "DRE Padrão" template. */
export interface FpaStandardContentResult {
  createdRollups: number;
  createdTemplates: number;
  /** Seed entries skipped because a tenant rollup already owns the default name. */
  nameConflicts: string[];
  /** Created or already-present standard templates, one for each requested fonte. */
  templates: FpaStandardContentTemplate[];
}

// ── Snapshots (ADR 0009) ─────────────────────────────────────────────────────
// Immutable as-of audit artifacts — never the serving path. Reads always
// evaluate live; every snapshot response carries its snapshotDate so snapshot
// reads can never masquerade as the current report.

/**
 * Snapshot metadata without the frozen report body. `snapshotDate` is the
 * as-of identity of the artifact; `manual` distinguishes on-demand freezes
 * from the daily worker's snapshots.
 */
export interface FpaSnapshotSummaryResponse {
  snapshotId: string;
  templateId: string;
  /** Open scenario-provider key (ADR 0008); the daily worker snapshots Realizado. */
  scenarioKind: string;
  budgetId?: string | null;
  /**
   * Forecast rule set the frozen forecast column was evaluated against (ALC-2116).
   * Rule sets are mutable, so this pin is what keeps a published board pack
   * explainable after somebody edits the set.
   */
  forecastRuleSetId?: string | null;
  entityIds: string[];
  /**
   * Ledger book the frozen report was evaluated against — the artifact's
   * immutable accounting scope, together with entityIds and scenarioKind.
   */
  bookId?: string | null;
  periodFrom: FpaPeriodKey;
  periodTo: FpaPeriodKey;
  /** ISO date (YYYY-MM-DD). Mandatory in every snapshot-scoped response. */
  snapshotDate: string;
  manual: boolean;
  label?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

/** Input for comparing one frozen forecast artifact with live actuals. */
export interface FpaForecastAccuracyRequest {
  templateId: string;
  /** Selects the frozen artifact identity; it is not an index into its cells. */
  scenarioRef: FpaScenarioRef;
  /** Latest snapshot date eligible for the artifact lookup (YYYY-MM-DD). */
  asOfDate: string;
  /** Inclusive calendar-month range to score. */
  periodFrom?: FpaPeriodKey;
  /** Inclusive calendar-month range to score. */
  periodTo?: FpaPeriodKey;
}

export interface FpaForecastAccuracyMonth {
  period: FpaPeriodKey;
  forecast: string | null;
  actual: string | null;
  /** `actual - forecast`; null when the frozen artifact has no cell. */
  error: string | null;
  absoluteError: string | null;
  /** `abs(error) / abs(actual) * 100`; null when actual is zero or unavailable. */
  absolutePercentageError: string | null;
}

export interface FpaForecastAccuracyLine {
  rowId: string;
  rollupId?: string | null;
  label?: string | null;
  months: FpaForecastAccuracyMonth[];
}

export interface FpaForecastAccuracyNotYetScorablePeriod {
  period: FpaPeriodKey;
  /** Every entity that is still OPEN, LOCKED nowhere, or lacks that period. */
  openEntityIds: string[];
}

export interface FpaForecastAccuracyResponse {
  templateId: string;
  scenarioRef: FpaScenarioRef;
  requestedAsOfDate: string;
  artifact: {
    snapshotId: string;
    snapshotDate: string;
    capturedAt: string;
    manual: boolean;
    entityIds: string[];
    bookId: string;
    /** Present when the eligible artifact predates the requested as-of date. */
    note?: string;
  };
  /** Error and bias use `actual - forecast`. */
  signConvention: 'error = actual - forecast; bias = mean signed error';
  lines: FpaForecastAccuracyLine[];
  notYetScorable: FpaForecastAccuracyNotYetScorablePeriod[];
  overall: {
    /** Number of frozen/live pairs included in signed-error aggregates. */
    scoredTerms: number;
    /** Cells excluded from MAPE because their actual is exactly zero. */
    mapeExcludedCells: number;
    /** Null when every scorable pair has a zero actual. */
    mape: string | null;
    mapeUnavailableReason?: 'ALL_ACTUALS_ZERO';
    /** Mean signed error across scored terms. */
    bias: string | null;
  };
}

/**
 * The definitions that produced a frozen report, stored with it (ALC-1910,
 * CFO-01). Rollups and templates stay editable and evaluation is live
 * (ADR 0009), so a reported number is only explainable afterwards if the
 * definition travels with the artifact.
 */
export interface FpaSnapshotDefinition {
  /**
   * Payload format version. Snapshots are immutable and the trigger forbids
   * backfill, so readers of old artifacts must branch on this — never assume
   * the live DTO shapes below match what an old artifact stored (the
   * pre-regime cells lesson). Bump when the stored shape changes.
   */
  schemaVersion: 1 | 2;
  template: FpaTemplateDetailResponse;
  /**
   * Every rollup the template's rows reference, plus formula/parent
   * dependencies transitively, as of the freeze.
   */
  rollups: FpaRollupResponse[];
  /**
   * Premissas applied to the frozen scenario at capture time (`schemaVersion`
   * ≥ 2). Empty when the scenario binds none (REALIZADO).
   */
  assumptions?: FpaAssumptionResponse[];
  /**
   * Line overrides applied to this template × scenario at capture time
   * (`schemaVersion` ≥ 2).
   */
  overrides?: FpaLineOverrideResponse[];
}

export interface FpaResultSnapshotResponse extends FpaSnapshotSummaryResponse {
  cells: FpaEvaluatedReport;
  /**
   * Frozen definition payload (CFO-01). Null for artifacts frozen before
   * ALC-1910 introduced definition freezing.
   */
  definition?: FpaSnapshotDefinition | null;
  /**
   * Whether this artifact carries a frozen per-cell decomposition (ALC-2608).
   * A plain boolean, never the decomposition itself — that stays server-side and
   * is served one cell at a time by the dedicated drill-down read. `false` for
   * every artifact captured before ALC-2608 (and for the exotic case where every
   * cell failed its capture-time reconciliation assertion). The web client uses
   * this ALONE to decide whether the grid's cells are clickable at all; even when
   * `true`, an individual cell can still refuse (a flagged mismatch, or a formula
   * operand ALC-2608 could not resolve cheaply) — that refusal is per-cell, from
   * the drill-down read itself, not predicted here.
   */
  hasFrozenDrilldown: boolean;
  /**
   * Count of cells the capture-time decomposition pass flagged and skipped
   * (reconciliation {@link FpaSnapshotDecompositionMismatch}es plus
   * {@link FpaSnapshotDecompositionFailure}s), never the entries themselves —
   * those stay server-side beside `mismatches`/`failures` were an audit trail,
   * not a response payload. `0` for a fully-decomposed artifact and for every
   * pre-ALC-2608 artifact (no decomposition pass ran at all). Not on
   * {@link FpaSnapshotSummaryResponse}: `listSnapshots` omits the `cells`
   * payload entirely for cost, and computing this count would force reading it
   * back per row.
   */
  decompositionMismatchCount: number;
}

/**
 * One cell's frozen composition, captured once at freeze time (ALC-2608 / ACC-03)
 * so an as-of drill-down reproduces the FROZEN rows instead of decomposing
 * whatever the underlying documents look like today.
 *
 * Stored server-side beside a snapshot's `cells` payload — never echoed on
 * {@link FpaResultSnapshotResponse}, which stays exactly `FpaEvaluatedReport` so
 * routine snapshot reads (`getSnapshot`, `getSnapshotAsOf`, `diffSnapshots`) do
 * not balloon with every cell's full row set. Served one cell at a time by the
 * dedicated snapshot drill-down read.
 */
export interface FpaSnapshotCellDecomposition {
  /** Capped at capture time (see the capturing service for the exact cap). */
  rows: FpaDrilldownRow[];
  /**
   * TRUE row count before the cap — may exceed `rows.length`. Renderers must
   * show "N de rowCount" rather than asserting `rows.length` is the whole story,
   * the same honesty rule live coverage truncation already follows.
   */
  rowCount: number;
  /**
   * The frozen cell's exact value, echoed rather than re-derived from `rows`
   * (formula-operand and metric rows never sum to their cell — see
   * `FpaDrilldownResult.reconcilingTotal`). Asserted equal to the frozen cell's
   * `value` at capture time; a cell that failed that assertion has no entry here
   * at all (ALC-2608 — flag-and-skip, never store a total that does not
   * reconcile).
   */
  reconcilingTotal: string;
  /** Display format of the frozen cell; absent = CURRENCY (legacy entries). */
  format?: FpaDisplayFormat | null;
  /** Present only for CURRENCY-format cells — a percent/number total has no currency. */
  currency?: string;
  /** Present when this cell decomposed a CALCULATED rollup into its operands. */
  formula?: FpaFormulaNode | null;
}

/**
 * One capture-time reconciliation failure, kept for audit instead of silently
 * dropped or silently stored wrong (ACC-03). The cell itself gets no
 * {@link FpaSnapshotCellDecomposition} entry — the as-of drill-down refuses it
 * exactly like a pre-ALC-2608 artifact would.
 */
export interface FpaSnapshotDecompositionMismatch {
  rowId: string;
  /** `${columnIndex}:${period}`, matching `FpaEvaluatedRow.cells` keys. */
  cellKey: string;
  code: string;
  cellValue: string;
  computedTotal: string;
}

/**
 * One capture-time DECOMPOSITION failure — the provider (or formula) path THREW
 * while decomposing this cell, rather than returning a total that disagreed with
 * the frozen value (that case is {@link FpaSnapshotDecompositionMismatch}
 * instead). Every provider that does not implement cell decomposition (today:
 * every scenario provider except budget and revenue) throws
 * `FPA_DRILLDOWN_SCENARIO_UNSUPPORTED` for every AGGREGATION cell it owns — this
 * is the expected, common case, not an anomaly — so capture must flag-and-skip
 * the cell (ACC-03) instead of failing the entire artifact. `code` is the
 * error's own stable `code` (never a message substring); an error with no stable
 * code gets a generic one.
 */
export interface FpaSnapshotDecompositionFailure {
  rowId: string;
  /** `${columnIndex}:${period}`, matching `FpaEvaluatedRow.cells` keys. */
  cellKey: string;
  code: string;
}

/**
 * Per-cell frozen decompositions, keyed by rowId → cell key (`${columnIndex}:${period}`,
 * matching `FpaEvaluatedRow.cells` / `FpaBucketRow.cells`). A snapshot's `columns` array
 * always has exactly one entry (`captureSnapshot` evaluates a single `[{ scenarioRef,
 * periods }]` column set), so `columnIndex` is always `0` — but the key format still
 * matches the report's own cell keys rather than assuming that.
 *
 * Absent entirely on artifacts captured before ALC-2608 — callers must not fabricate a
 * decomposition for those (ADR 0009: a snapshot is never re-derived after the freeze).
 */
export interface FpaSnapshotDecompositionIndex {
  schemaVersion: 1;
  byRow: Record<string, Record<string, FpaSnapshotCellDecomposition>>;
  /** Present only when at least one cell failed the reconciliation assertion at capture. */
  mismatches?: FpaSnapshotDecompositionMismatch[];
  /**
   * Present only when at least one cell's decomposition attempt threw (ALC-2608
   * follow-up) — a provider with no `drilldownCell`, a CALCULATED cell with no
   * resolvable operand, an FX-pin refusal, etc. Sibling of `mismatches`: both are
   * flag-and-skip, never a reason to fail the whole capture.
   */
  failures?: FpaSnapshotDecompositionFailure[];
}

/**
 * What is actually persisted in `FpaResultSnapshot.cells` (ALC-2608): the evaluated
 * report the public response type already promises, plus — for artifacts captured
 * after this feature landed — the frozen per-cell decomposition index. Server-internal:
 * `FpaResultSnapshotResponse.cells` stays exactly `FpaEvaluatedReport`.
 */
export interface FpaSnapshotStoredCells extends FpaEvaluatedReport {
  /** Immutable fingerprint of the scenario rules used when capturing this artifact. */
  payrollScenarioRevision?: string;
  /** Immutable capture provenance. Legacy artifacts are UNKNOWN, never reclassified using live rules. */
  payrollVisibility?: { schemaVersion: 1; state: 'NONE' | 'PRESENT' | 'UNKNOWN' };
  decomposition?: FpaSnapshotDecompositionIndex;
}

/**
 * Response of the as-of cell drill-down (ALC-2608): the frozen composition for one
 * cell of one snapshot, clearly marked so a caller can never mistake it for a live
 * decomposition. No `pagination` — stored rows are capped with no server-side
 * filter/sort, so the drawer renders `rowCount` as a truncation disclosure instead of
 * paging controls that would imply more can be fetched.
 */
export interface FpaSnapshotDrilldownResult {
  handle: FpaCellDrilldownHandle;
  rows: FpaDrilldownRow[];
  rowCount: number;
  reconcilingTotal: string;
  /** Display format of the frozen cell; absent = CURRENCY (legacy snapshots). */
  format?: FpaDisplayFormat | null;
  /** Present only for CURRENCY-format cells — a percent/number total has no currency. */
  currency?: string;
  formula?: FpaFormulaNode | null;
  source: 'SNAPSHOT';
  /** The snapshot's own freeze date (`YYYY-MM-DD`) — labels the frozen-composition banner. */
  snapshotDate: string;
}

/** On-demand freeze ("before the board meeting"). Defaults mirror the daily worker. */
export interface FpaFreezeSnapshotRequest {
  /** Defaults to every entity of the tenant. */
  entityIds?: string[];
  /** Defaults to the trailing 13 periods ending in the current month. */
  periods?: FpaPeriodKey[];
  /** Ledger book to evaluate; defaults to the designated/shared book resolution. */
  bookId?: string | null;
  label?: string | null;
  /**
   * Scenario to freeze (ALC-2404); defaults to REALIZADO. A rule-backed kind pins its
   * `forecastRuleSetId` onto the artifact, which is what keeps a published board pack
   * explainable after somebody edits that mutable rule set. Only manual freezes may name
   * a non-REALIZADO scenario — the daily lane captures REALIZADO only.
   */
  scenarioRef?: FpaScenarioRef;
}

export interface FpaListSnapshotsQuery {
  templateId?: string;
  /** Inclusive snapshotDate lower bound (YYYY-MM-DD). */
  from?: string;
  /** Inclusive snapshotDate upper bound (YYYY-MM-DD). */
  to?: string;
  /** Newest-first cap; the daily series grows by one artifact per template per day. */
  limit?: number;
}

/**
 * One changed cell between two snapshots of the same template. Cells are
 * matched by (rollupId | bucket, columnKey); `drilldown` links the cell to its
 * live drill-down (always live rows — ADR 0009).
 */
export interface FpaSnapshotCellDelta {
  /** Rollup identity of the row, or null for bucket rows. */
  rollupId?: string | null;
  /** Bucket identity when the row is an unattributable bucket. */
  bucket?: 'naoClassificado' | 'semEventoDeCaixa' | null;
  /** Cell key inside the snapshot report, `${columnIndex}:${period}`. */
  columnKey: string;
  period: FpaPeriodKey;
  /** Value in the `from` snapshot; null when the cell only exists in `to`. */
  fromValue: string | null;
  /** Value in the `to` snapshot; null when the cell only exists in `from`. */
  toValue: string | null;
  /** Exact numeric delta when both snapshots have a value; null for availability changes. */
  delta: string | null;
  /** Explicitly distinguishes missing data from a numeric zero. */
  availabilityChange?: 'BECAME_AVAILABLE' | 'BECAME_UNAVAILABLE' | null;
  /** Display format of the `to` (or only) side; absent = CURRENCY (bucket rows and legacy snapshots). */
  format?: FpaDisplayFormat | null;
  /**
   * Present only when the row's display format CHANGED between the two
   * snapshots: the `from` side's format. The two values are then in different
   * units — `delta` is null and the sides must each render in their own format.
   */
  fromFormat?: FpaDisplayFormat | null;
  /** Present only when the source row's display format is CURRENCY. */
  currency?: string;
  /** `from`-side currency when the format changed and the `from` side was CURRENCY. */
  fromCurrency?: string;
  drilldown?: FpaCellDrilldownHandle | null;
}

/** Diff between two as-of snapshots (first-class per ADR 0009). */
export interface FpaSnapshotDiffResponse {
  templateId: string;
  from: FpaSnapshotSummaryResponse;
  to: FpaSnapshotSummaryResponse;
  /** Only cells whose value changed (or appeared/disappeared) between the two. */
  deltas: FpaSnapshotCellDelta[];
}

// ── Plano de Contratações (ALC-2117) ─────────────────────────────────────────

export interface FpaHeadcountPositionResponse {
  positionId: string;
  headcountPlanId: string;
  entityId: string;
  title: string;
  departmentId: string | null;
  costCenterId: string | null;
  hiringType: FpaHeadcountHiringType;
  managerialAccountId: string | null;
  /** Inclusive start month, `YYYY-MM`. */
  startMonth: FpaPeriodKey;
  /** Inclusive end month, `YYYY-MM`. Null = open-ended. */
  endMonth: FpaPeriodKey | null;
  /** Flat monthly salary as a decimal string. */
  salary: string;
  /** Functional currency stamped on the canonical hiring plan. */
  currency: string;
  status: FpaHeadcountPositionStatus;
  employeeId: string | null;
  /** The PAYROLL recurring-bill template a PJ hire is confirmed against. Null for CLT. */
  vendorRecurringBillId: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface FpaHeadcountPlanDetailResponse {
  headcountPlanId: string;
  entityId: string;
  /** Functional currency stamped at plan creation. Evaluation never converts. */
  currency: string;
  createdAt: string;
  updatedAt?: string | null;
  positions: FpaHeadcountPositionResponse[];
}

export interface FpaUpsertHeadcountPlanRequest {
  entityId: string;
}

export interface FpaCreateHeadcountPositionRequest {
  title: string;
  departmentId?: string | null;
  costCenterId?: string | null;
  hiringType?: FpaHeadcountHiringType;
  managerialAccountId?: string | null;
  startMonth: FpaPeriodKey;
  endMonth?: FpaPeriodKey | null;
  salary: string;
  status?: FpaHeadcountPositionStatus;
}

export interface FpaUpdateHeadcountPositionRequest {
  title?: string;
  departmentId?: string | null;
  costCenterId?: string | null;
  hiringType?: FpaHeadcountHiringType;
  managerialAccountId?: string | null;
  startMonth?: FpaPeriodKey;
  endMonth?: FpaPeriodKey | null;
  salary?: string;
  status?: FpaHeadcountPositionStatus;
  /**
   * "Alterar vínculo": swaps the linked contract on an already-HIRED PJ Posição. Only valid on
   * a HIRED PJ position; a PLANNED position or a CLT one must go through confirm-hire instead.
   */
  vendorRecurringBillId?: string;
}

/**
 * CLT confirms hire with `employeeId`; PJ confirms hire with `vendorRecurringBillId` — the two
 * are mutually exclusive and the position's `hiringType` decides which is required.
 */
export interface FpaConfirmHeadcountHireRequest {
  employeeId?: string;
  vendorRecurringBillId?: string;
}

export interface FpaHeadcountHireSuggestion {
  positionId: string;
  employeeId: string;
  employeeLabel: string | null;
  score: number;
  reasons: string[];
}

/**
 * A PAYROLL contract suggested as the hire for a PLANNED PJ Posição ("Door 1", ALC-1902).
 * Confirm with `vendorRecurringBillId`, not `employeeId`.
 */
export interface FpaHeadcountContractHireSuggestion {
  positionId: string;
  vendorRecurringBillId: string;
  vendorLabel: string | null;
  /** Contract monthly value, decimal string. */
  salary: string;
  score: number;
}

export interface FpaHeadcountHireSuggestionsResponse {
  positions: FpaHeadcountHireSuggestion[];
  contracts: FpaHeadcountContractHireSuggestion[];
}

export interface FpaHeadcountMonthCell {
  period: FpaPeriodKey;
  departmentId: string | null;
  costCenterId: string | null;
  heads: number;
  cost: string;
  currency: string;
  bucket: 'PLANNED' | 'HIRED' | 'UNPLANNED';
  costBasis: 'PAYROLL_RESULT' | 'PLAN_EMPLOYER_COST';
}

/** One plan-linked position's monthly cost components. */
export interface FpaHeadcountPositionMonth {
  positionId: string;
  period: FpaPeriodKey;
  departmentId: string | null;
  costCenterId: string | null;
  bucket: 'PLANNED' | 'HIRED';
  costBasis: 'PAYROLL_RESULT' | 'PLAN_EMPLOYER_COST';
  /** Driving salary before employer charges, as a decimal string. */
  salary: string;
  /** Brazilian employer charges and provisions, as a decimal string. */
  encargos: string;
  /** salary + encargos, as a decimal string. */
  total: string;
  currency: string;
}

/** Position identifiers covered by the report's canonical plan. */
export interface FpaHeadcountPlanCoverage {
  inputId: string;
  kind: 'POSITION';
}

/** Visible "não planejado" department sentinel — share with the web report. */
export const HEADCOUNT_UNPLANNED_DEPARTMENT = '__UNPLANNED__';

/**
 * One forward (or gap) month of Equipe atual after HIRED-plan overlap is
 * removed for that month. Not plan data — display-only (ALC-2117 UX).
 */
export interface FpaHeadcountCurrentTeamMonthContribution {
  period: FpaPeriodKey;
  heads: number;
  /** Plan employer cost of the remaining people (`deriveMonthlyPersonnelEncargos`), decimal string. */
  cost: string;
  /** Posted MONTHLY run this month carries (`YYYY-MM`). */
  sourcePeriod: FpaPeriodKey;
  /** Optional dimension breakdown of the carried team for this month. */
  breakdown?: FpaHeadcountCurrentTeamBreakdown[];
}

export interface FpaHeadcountCurrentTeamBreakdown {
  departmentId: string | null;
  costCenterId: string | null;
  heads: number;
  cost: string;
}

/**
 * Last posted MONTHLY payroll snapshot, carried forward at run-rate on the
 * Plano de Contratações report. Contributions are per month so a HIRED
 * Posição is never double-counted during its overlap window and is not
 * dropped after `endMonth`. Not plan data — display-only (ALC-2117 UX).
 */
export interface FpaHeadcountCurrentTeamRunRate {
  /** Latest posted MONTHLY payroll run overall (`YYYY-MM`). */
  sourcePeriod: FpaPeriodKey;
  currency: string;
  /** Posted MONTHLY periods (realized — Equipe atual is a dash, not carry). */
  postedPeriods: FpaPeriodKey[];
  /**
   * Per-month Equipe atual after excluding employees covered by a HIRED
   * plan cell in that month. Realized months are omitted. A 0-head month
   * means the standing team is fully inside the plan that month.
   */
  months: FpaHeadcountCurrentTeamMonthContribution[];
}

/** One projected month of active PJ payroll recurring bills. */
export interface FpaHeadcountCurrentTeamPjMonthContribution {
  period: FpaPeriodKey;
  heads: number;
  /** Contracted recurring-bill value; no CLT encargos are added. */
  cost: string;
  /** Optional dimension breakdown of the carried PJ team for this month. */
  breakdown?: FpaHeadcountCurrentTeamBreakdown[];
}

/** Active PJ payroll recurring bills, projected within their contract windows. */
export interface FpaHeadcountCurrentTeamPjRunRate {
  currency: string;
  months: FpaHeadcountCurrentTeamPjMonthContribution[];
}

export const FpaHeadcountEncargosPolicySource = {
  TENANT_CONFIGURED: 'TENANT_CONFIGURED',
  DEFAULT_TENANT_POLICY: 'DEFAULT_TENANT_POLICY',
} as const;
export type FpaHeadcountEncargosPolicySource =
  (typeof FpaHeadcountEncargosPolicySource)[keyof typeof FpaHeadcountEncargosPolicySource];

/** Employer-cost explanation for the headcount UI (salary × multiplier). */
export interface FpaHeadcountEncargosDisplay {
  /** Exact fraction, e.g. `"1.528888"`. */
  employerCostMultiplier: string;
  /** Sum of CPP + RAT/FAP + terceiros + FGTS, e.g. `"0.2800"`. */
  statutoryRate: string;
  policySource: FpaHeadcountEncargosPolicySource;
}

export interface FpaHeadcountReportResponse {
  headcountPlanId: string;
  entityId: string;
  currency: string;
  asOfMonth: FpaPeriodKey;
  from: FpaPeriodKey;
  to: FpaPeriodKey;
  cells: FpaHeadcountMonthCell[];
  enumerableInputs: { inputId: string; kind: 'POSITION' }[];
  /** Per-position salary, charges, and total for each emitted month. */
  positionMonths: FpaHeadcountPositionMonth[];
  /** Explicit plan-input coverage for MCP and other non-UI consumers. */
  coverage: FpaHeadcountPlanCoverage[];
  /** Last posted payroll run-rate, or null when no posted MONTHLY run exists. */
  currentTeamRunRate: FpaHeadcountCurrentTeamRunRate | null;
  /** Active PJ payroll recurring bills, independent from HCM payroll results. */
  currentTeamPjRunRate?: FpaHeadcountCurrentTeamPjRunRate | null;
  encargos: FpaHeadcountEncargosDisplay;
}

// ── Premissas + line overrides (ALC-2118) ────────────────────────────────────

export interface FpaAssumptionValueInput {
  period: FpaPeriodKey;
  /** Decimal serialized as string. */
  value: string;
}

export interface FpaAssumptionValueResponse {
  period: FpaPeriodKey;
  value: string;
}

export interface FpaAssumptionResponse {
  assumptionId: string;
  assumptionKey: string;
  name: string;
  unit: string;
  provenance?: string | null;
  budgetId?: string | null;
  forecastRuleSetId?: string | null;
  values: FpaAssumptionValueResponse[];
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export interface FpaCreateAssumptionRequest {
  assumptionKey: string;
  name: string;
  unit: string;
  provenance?: string | null;
  budgetId?: string | null;
  forecastRuleSetId?: string | null;
  values: FpaAssumptionValueInput[];
}

export interface FpaUpdateAssumptionRequest {
  name?: string;
  unit?: string;
  provenance?: string | null;
  /**
   * Per-period upsert merge. Months omitted here are kept. Use
   * {@link FpaUpdateAssumptionRequest.removePeriods} to delete months.
   */
  values?: FpaAssumptionValueInput[];
  /** Periods to delete. Applied before `values` so an explicit upsert wins. */
  removePeriods?: FpaPeriodKey[];
  changeReason?: string | null;
}

export interface FpaListAssumptionsQuery {
  budgetId?: string;
  forecastRuleSetId?: string;
  assumptionKey?: string;
  includeDeleted?: boolean;
  take?: number;
  offset?: number;
  includeValues?: boolean;
}

export interface FpaLineOverrideResponse {
  overrideId: string;
  templateId: string;
  rollupId: string;
  /** Decimal serialized as string. */
  amount: string;
  effectiveFrom: FpaPeriodKey;
  effectiveTo?: string | null;
  entityIds: string[];
  bookId: string;
  currency: string;
  provenance?: string | null;
  budgetId?: string | null;
  forecastRuleSetId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface FpaCreateLineOverrideRequest {
  templateId: string;
  rollupId: string;
  amount: string;
  effectiveFrom: FpaPeriodKey;
  effectiveTo?: FpaPeriodKey | null;
  entityIds: string[];
  bookId: string;
  provenance?: string | null;
  budgetId?: string | null;
  forecastRuleSetId?: string | null;
}

export interface FpaAssumptionListResponse {
  items: FpaAssumptionResponse[];
  take: number;
  total: number;
  hasMore: boolean;
  nextOffset: number | null;
}

export interface FpaLineOverrideListResponse {
  items: FpaLineOverrideResponse[];
  take: number;
  total: number;
  hasMore: boolean;
  nextOffset: number | null;
}

export interface FpaListLineOverridesQuery {
  templateId?: string;
  budgetId?: string;
  forecastRuleSetId?: string;
  take?: number;
  offset?: number;
}

// ── Driver-derived budget limits (ALC-2537) ──────────────────────────────────
// Approve-to-materialize: the rule is metadata; stamps write static amounts.
// Bill-time enforcement never evaluates the formula.

export interface FpaDerivationRuleResponse {
  budgetLineId: string;
  budgetId: string;
  formula: FpaFormulaNode | null;
  /** Pinned author of the current formula. Null until a real save (copy / legacy). */
  ruleAuthorUserId: string | null;
}

export interface FpaPutDerivationRuleRequest {
  formula: FpaFormulaNode | null;
}

export interface FpaDerivedLimitAssumptionValue {
  assumptionKey: string;
  name: string;
  unit: string;
  period: FpaPeriodKey;
  /** Decimal serialized as string. */
  value: string;
}

export interface FpaDerivedLimitMetricValue {
  metricKey: string;
  seriesId: string | null;
  entityId: string | null;
  period: FpaPeriodKey;
  /** Decimal serialized as string. */
  value: string;
  /** Series currency when the metric is nominally denominated; null for counts. */
  currencyCode?: string | null;
}

export interface FpaDerivedLimitUnresolved {
  kind: 'assumption' | 'metric';
  key: string;
  period: FpaPeriodKey;
}

export type FpaDerivedLimitAnnotationKind = 'PARENT_SUM_EXCEEDS';

export interface FpaDerivedLimitAnnotation {
  kind: FpaDerivedLimitAnnotationKind;
  childrenSum: string;
  parentAmount: string;
  childBudgetLineIds: string[];
}

export interface FpaDerivedLimitPeriod {
  period: FpaPeriodKey;
  /** Suggested value from the live formula; null when unresolved. */
  derivedAmount: string | null;
  /** Enforcing cell limit (explicit row → perPeriodAmount → amount / covered). */
  approvedAmount: string | null;
  /** On-evaluate only — derived !== approved. Nothing stored. */
  drifted: boolean;
  unresolved: FpaDerivedLimitUnresolved[];
  assumptionValues: FpaDerivedLimitAssumptionValue[];
  metricValues: FpaDerivedLimitMetricValue[];
  annotations: FpaDerivedLimitAnnotation[];
}

export interface FpaDerivedLimitReviewQuery {
  periodFrom?: FpaPeriodKey;
  periodTo?: FpaPeriodKey;
}

export interface FpaDerivedLimitReviewResponse {
  budgetLineId: string;
  budgetId: string;
  formula: FpaFormulaNode | null;
  /** Pinned author of the current formula. Null until a real save (copy / legacy). */
  ruleAuthorUserId: string | null;
  periods: FpaDerivedLimitPeriod[];
}

export interface FpaApproveDerivedLimitsRequest {
  periodFrom: FpaPeriodKey;
  /** Inclusive. Omit or equal periodFrom for a single period. */
  periodTo?: FpaPeriodKey;
  /**
   * Amount the reviewer saw per period. Approve re-derives inside the
   * transaction and refuses unless `Decimal.equals` holds for every period.
   * Bulk-approve is atomic: one mismatch or unresolved period aborts the range.
   */
  expectedAmounts: { period: FpaPeriodKey; amount: string }[];
}

export interface FpaApprovedDerivedLimitPeriod {
  period: FpaPeriodKey;
  /** Stamped Decimal(18,2) as string. */
  stampedAmount: string;
}

export interface FpaApproveDerivedLimitsResponse {
  budgetLineId: string;
  budgetId: string;
  periods: FpaApprovedDerivedLimitPeriod[];
}
